import { setFocus } from '@noriginmedia/norigin-spatial-navigation'
import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useIsLocalizationVerificationEnabled } from '../../../../shared/providers/FeatureTogglesProvider/useIsLocalizationVerificationEnabled'
import {
  getLocalizedStations,
  getSchedulesByStation,
} from '../../actions/contentActions.js'
import { useSidePanel } from '../../components/SidePanel/SidePanelProvider'
import Spinner from '../../components/Spinner'
import { useAppDispatch, useAppSelector } from '../../hooks/useStore'
import ErrorModalProvider from '../VideoDetailScreen/context/ErrorModalProvider'
import useGetExternalIP from './hookToRemoveLater/useGetExternalIP'
import LiveTVUnavailable from './LiveTVUnavailable'
import LiveTVWithEPG from './LiveTVWithEPG'
import LiveTVWithGPSVerification from './LiveTVWithGPSVerification'
import LiveTVWithStations from './LiveTVWithStations'
import { IPS as whiteListedIPS } from './LocalizationIPListToIgnore'
import type { Station } from './types'

declare const COMPILING_FOR_LGTV: boolean

function LivestreamScreen() {
  const [stationsLoaded, setStationsLoaded] = useState(false)
  const [stations, setStations] = useState<Array<Station>>([])
  const [isUserInAssociatedStations, setIsUserInAssociatedStations] =
    useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { call_sign: callSign } = useAppSelector((state) => state.station)
  const channels = useAppSelector((state) => state.multilivestream.channels)
  const { ip: userExternalIp, loading: isIPFetchingLoading } =
    useGetExternalIP()
  const fallbackStation = useAppSelector(
    (state) => state.station.fallback_station
  )
  const dispatch = useAppDispatch()
  const { setKeepSidePanelOpened } = useSidePanel()
  const openedFromLocalizationError = Boolean(
    location.state?.fromLocalizationError
  )
  const {
    isLocalizationVerificationEnabled,
    isLoading: loadingGPSFeatureFlag,
  } = useIsLocalizationVerificationEnabled()

  const isScreenOpenedFromChannelPlayback = () =>
    location.search.includes('fromChannelPlayback=true')

  const clearFallbackStation = () => {
    dispatch({
      type: 'UPDATE_FALLBACK_STATION',
      payload: {
        callsign: '',
        cid: '',
        city: '',
        fullName: '',
        shortName: '',
      },
    })
  }

  const userInAssociatedStations = useCallback(async () => {
    const { watchChannelFromHome } = location.state || {}
    try {
      const result = await getLocalizedStations()
      const localizedStations = (result && result.stations) || []

      const userIsInAssociatedStations = localizedStations
        .map((station: Station) => station.callsign)
        .includes(callSign)

      if (watchChannelFromHome && userIsInAssociatedStations) {
        navigate('/live-playback', {
          state: {
            channel: watchChannelFromHome,
          },
          replace: true,
        })
        return
      }
      setFocus('livestream')
      setStationsLoaded(true)
      setIsUserInAssociatedStations(userIsInAssociatedStations)
      setStations(
        localizedStations.map((x: any) => ({
          callsign: x.callsign,
          cid: x.station_id,
          city: x.city,
          fullName: x.common_name_full,
          shortName: x.common_name_short,
        }))
      )
    } catch (error) {
      setStationsLoaded(true)
    }
  }, [location.state, callSign, navigate])

  useEffect(() => {
    setKeepSidePanelOpened(true)
    if (!isScreenOpenedFromChannelPlayback() && !openedFromLocalizationError) {
      clearFallbackStation()
    }

    if (!COMPILING_FOR_LGTV) {
      userInAssociatedStations()
    }

    return () => setKeepSidePanelOpened(false)
  }, [userInAssociatedStations])

  const handleUserLocalized = () => {
    setIsUserInAssociatedStations(true)
  }

  const setDefaultStation = useCallback(() => {
    setStationsLoaded(true)
    setIsUserInAssociatedStations(true)
    setStations([
      {
        callsign: callSign,
        cid: '',
        city: '',
        fullName: '',
        shortName: '',
        resource: {},
      },
    ])
  }, [callSign])

  useEffect(() => {
    if (isIPFetchingLoading || userExternalIp === '') return
    if (COMPILING_FOR_LGTV) {
      const whiteListUser = whiteListedIPS.includes(userExternalIp)
      if (whiteListUser) {
        setDefaultStation()
      } else {
        userInAssociatedStations()
      }
    }
  }, [userExternalIp, isIPFetchingLoading, userInAssociatedStations])

  useEffect(() => {
    if (isUserInAssociatedStations && stationsLoaded && channels.length === 0) {
      const station = stations.find((s) => s.callsign === callSign)
      if (station?.cid) {
        getSchedulesByStation(
          station.cid,
          new Date().toISOString().split('T')[0],
          true
        )
      }
    }
  }, [isUserInAssociatedStations, stationsLoaded, channels, stations, callSign])

  const stationName =
    stations?.find((station) => station.callsign === callSign)?.shortName || ''

  const { callsign: fallbackStationCallsign, shortName: fallbackStationName } =
    fallbackStation

  return !stationsLoaded || loadingGPSFeatureFlag ? (
    <Spinner />
  ) : (
    <div className="livestream-screen">
      {!isUserInAssociatedStations &&
        stations.length > 0 &&
        (!fallbackStationCallsign ? (
          <ErrorModalProvider>
            {isLocalizationVerificationEnabled ? (
              <LiveTVWithGPSVerification
                stations={stations}
                handleUserLocalized={handleUserLocalized}
              />
            ) : (
              <LiveTVWithStations stations={stations} />
            )}
          </ErrorModalProvider>
        ) : (
          <LiveTVWithEPG
            stationName={fallbackStationName}
            stealFocus={openedFromLocalizationError}
          />
        ))}
      {isUserInAssociatedStations && (
        <LiveTVWithEPG
          stationName={stationName}
          stealFocus={openedFromLocalizationError}
        />
      )}
      {stations.length === 0 && <LiveTVUnavailable navigate={navigate} />}
    </div>
  )
}

export default LivestreamScreen
